import { type Control, type FieldErrors, Controller, useController, useWatch } from 'react-hook-form';
import { Box, TextField, Typography } from '@mui/material';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import Grid from '@mui/material/Grid2';
import FieldLabel from './FieldLabel';
import ChipInput from './ChipInput';
import CodeRows, { type CodeRowsSuggestion } from './CodeRows';
import InlineAiSuggestion from './InlineAiSuggestion';
import SubblockHeading from './SubblockHeading';
import AnesthesiaTimeRange, { type AnesthesiaTimeRangeFields } from './AnesthesiaTimeRange';
import { GRID_SPACING, COL_2 } from '../constants/layout';
import type { ASAClassificationOption } from '../constants/asa';
import { formatDateForDisplay } from '../utils/dateValidation';
import { CLAIM_LEVEL_FIELD_DEFAULT, type FormFieldSetting } from '../constants/encounterFieldsConfig';
import { hasFieldValue } from '../utils/hasFieldValue';

/** Source region for one AI-extracted candidate — switches the document viewer to it. */
export interface CodingSuggestionSource {
  documentId: string;
  page: number;
  bbox: { x: number; y: number; width: number; height: number };
}

/** An AI-extracted scalar candidate (ASA classification, an anesthesia time, ICD-10 code). */
export interface CodingScalarSuggestion {
  value: string;
  source?: CodingSuggestionSource;
}

/**
 * AI-extracted suggestions for one claim's Coding block (feature 036 extension — closes the
 * gap left when coding moved to claim level: research.md R8 deferred these because, at the
 * time, no per-claim coding field existed to accept them into). Every key is optional and
 * independently rendered; omit the whole prop for forms with no extraction pipeline (e.g. the
 * portal). ICD-10/CPT/ASA-codes are lists — one InlineAiSuggestion per candidate not already
 * present in the claim's current list; accepting appends it rather than replacing the list.
 */
export interface CodingSuggestions {
  icd10?: CodingScalarSuggestion[];
  cpt?: CodeRowsSuggestion[];
  asaCodes?: CodeRowsSuggestion[];
  asaClassification?: CodingScalarSuggestion;
  /** Feature 044 — one suggestion per anesthesia window field. */
  anesthesiaStartDate?: CodingScalarSuggestion;
  anesthesiaStartTime?: CodingScalarSuggestion;
  anesthesiaEndDate?: CodingScalarSuggestion;
  anesthesiaEndTime?: CodingScalarSuggestion;
}

interface CodingSectionProps {
  control: unknown;
  /**
   * Form path prefix for this claim's coding object, e.g. `claimTypes.0.coding`
   * (portal) or `cases.0.coding` (backoffice). All fields bind under it.
   */
  fieldPrefix: string;
  /** Validation errors for THIS coding subtree (e.g. errors.claimTypes?.[i]?.coding). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  codingErrors?: FieldErrors<any>;
  /** True for an ANESTHESIA claim — shows the ASA block. */
  showAsa: boolean;
  /** ASA Classification dropdown options (P1–P6 or similar). */
  asaOptions: readonly ASAClassificationOption[];
  /** ISO date 'YYYY-MM-DD' — the anesthesia start date defaults to it (feature 044). */
  dateOfService: string;
  /** Subblock heading text; defaults to "Coding". */
  title?: string;
  /** AI-extracted suggestions for this claim's coding fields. Omit where no extraction data exists. */
  suggestions?: CodingSuggestions;
  /** Switches the document viewer to a suggestion's source region. */
  onLocate?: (documentId: string, page: number, bbox: CodingSuggestionSource['bbox'], label: string) => void;
  /** Disables suggestion accept/undo (data-mutating) while leaving locate usable. */
  locked?: boolean;
  /**
   * Feature 043 — per-field visibility/required from practice settings, keyed by
   * `icd10`/`cpt`/`asaClassification`/`anesthesiaStartDate`/`anesthesiaStartTime`/
   * `anesthesiaEndDate`/`anesthesiaEndTime`/`asaCodes`. A field omitted from the map (or an omitted
   * `fields` prop entirely) defaults to the catalog default (`CLAIM_LEVEL_FIELD_DEFAULT`),
   * so backoffice's usage — which passes no `fields` — is unaffected.
   */
  fields?: Partial<Record<string, FormFieldSetting>>;
  /**
   * Feature 043 — draws the top divider (margin/padding/border) that separates this
   * subblock from a preceding Providers subblock. Owned here (not by the caller)
   * because the section can self-hide when every field is not-visible-and-empty —
   * an externally-wrapped divider Box would still render its border with nothing
   * inside it.
   */
  showDividerAbove?: boolean;
  /**
   * Give the single-value anesthesia fields — ASA Classification and the four window
   * fields — a full-width line each instead of a narrow column. Set by the back office,
   * whose claim card is much narrower than the portal's bill-type card; the portal keeps
   * its multi-column layout. Does not affect the CPT / ASA code rows, whose code and
   * modifiers belong side by side at any width.
   */
  stacked?: boolean;
  /** Show the military-time hint under each time field. The back office turns it off. */
  showTimeHint?: boolean;
  /**
   * Hides the CPT block's "Add CPT Code" button and disables (but still shows) any
   * CPT codes already on the claim — for callers where CPT shouldn't be entered or
   * edited here (e.g. backoffice's intake team; a Coder handles CPT downstream once
   * assigned). Distinct from `fields.cpt.visible`, which hides the whole block
   * including existing data.
   */
  hideAddButton?: boolean;
}

/** Renders one InlineAiSuggestion for a scalar candidate, or nothing when absent. Module-scoped
 *  (not a closure inside CodingSection) so it isn't redefined — and InlineAiSuggestion isn't
 *  remounted, losing its accepted/undo state — on every CodingSection render. */
function ScalarSuggestion({
  candidate,
  currentValue,
  onAccept,
  label,
  locked,
  onLocate,
  displayValue,
}: {
  candidate: CodingScalarSuggestion | undefined;
  currentValue: string;
  onAccept: (value: string) => void;
  label: string;
  locked?: boolean;
  onLocate?: CodingSectionProps['onLocate'];
  displayValue?: string;
}) {
  if (!candidate) return null;
  return (
    <InlineAiSuggestion
      suggestion={candidate.value}
      currentValue={currentValue}
      displayValue={displayValue}
      onAccept={onAccept}
      locked={locked}
      onLocate={
        candidate.source && onLocate
          ? () => onLocate(candidate.source!.documentId, candidate.source!.page, candidate.source!.bbox, label)
          : undefined
      }
    />
  );
}

/**
 * Shared per-claim "Coding" subblock: embedded inside a claim's bill-type card (portal)
 * or claim card (backoffice). Contains:
 *   - ICD-10/Diagnosis chip input (optional)
 *   - CPT Code + Modifiers repeatable rows (optional)
 *   - ASA Classification, the anesthesia window (start date/time + end date/time), ASA Code
 *     rows (conditional on `showAsa` — only for the Anesthesia claim type)
 *
 * All fields bind under `fieldPrefix` (the claim's `coding` path), so the same component
 * serves every claim. Renders no outer Paper/section wrapper — the caller owns the card.
 */
export default function CodingSection({
  control,
  fieldPrefix,
  codingErrors,
  showAsa,
  asaOptions,
  dateOfService,
  title = 'Coding',
  suggestions,
  onLocate,
  locked,
  fields,
  showDividerAbove,
  stacked,
  showTimeHint,
  hideAddButton,
}: CodingSectionProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ctrl = control as Control<any>;
  const codingErrs = codingErrors ?? {};

  const setting = (key: string) => fields?.[key] ?? CLAIM_LEVEL_FIELD_DEFAULT[key];

  const startDateCtrl = useController({ name: `${fieldPrefix}.anesthesiaStartDate`, control: ctrl });
  const startTimeCtrl = useController({ name: `${fieldPrefix}.anesthesiaStartTime`, control: ctrl });
  const endDateCtrl = useController({ name: `${fieldPrefix}.anesthesiaEndDate`, control: ctrl });
  const endTimeCtrl = useController({ name: `${fieldPrefix}.anesthesiaEndTime`, control: ctrl });
  const [watchedIcd10, watchedCpt, watchedAsaClassification, watchedAsaCodes] = useWatch({
    control: ctrl,
    name: [`${fieldPrefix}.icd10`, `${fieldPrefix}.cpt`, `${fieldPrefix}.asaClassification`, `${fieldPrefix}.asaCodes`],
  });

  // Feature 043 — a field with a saved value stays visible even when practice
  // settings mark it not-visible (hiding real data would look like data loss).
  const icd10Setting = { ...setting('icd10'), visible: setting('icd10').visible || hasFieldValue(watchedIcd10) };
  const cptSetting = { ...setting('cpt'), visible: setting('cpt').visible || hasFieldValue(watchedCpt) };
  const asaClassificationSetting = {
    ...setting('asaClassification'),
    visible: setting('asaClassification').visible || hasFieldValue(watchedAsaClassification),
  };
  // The two date fields default from Date of Service / the start date, so a plain
  // hasFieldValue check would report "has value" for a default the biller never chose.
  // Only a date differing from what it defaults from reflects a genuine answer.
  const startDateIsGenuine =
    !!startDateCtrl.field.value && startDateCtrl.field.value !== dateOfService;
  const endDateIsGenuine =
    !!endDateCtrl.field.value && endDateCtrl.field.value !== (startDateCtrl.field.value || dateOfService);
  const anesthesiaStartDateSetting = {
    ...setting('anesthesiaStartDate'),
    visible: setting('anesthesiaStartDate').visible || startDateIsGenuine,
  };
  const anesthesiaStartTimeSetting = {
    ...setting('anesthesiaStartTime'),
    visible: setting('anesthesiaStartTime').visible || hasFieldValue(startTimeCtrl.field.value),
  };
  const anesthesiaEndDateSetting = {
    ...setting('anesthesiaEndDate'),
    visible: setting('anesthesiaEndDate').visible || endDateIsGenuine,
  };
  const anesthesiaEndTimeSetting = {
    ...setting('anesthesiaEndTime'),
    visible: setting('anesthesiaEndTime').visible || hasFieldValue(endTimeCtrl.field.value),
  };
  const asaCodesSetting = { ...setting('asaCodes'), visible: setting('asaCodes').visible || hasFieldValue(watchedAsaCodes) };
  const showAnesthesiaTimeRange =
    anesthesiaStartDateSetting.visible ||
    anesthesiaStartTimeSetting.visible ||
    anesthesiaEndDateSetting.visible ||
    anesthesiaEndTimeSetting.visible;

  const showAsaBlock = showAsa && (asaClassificationSetting.visible || showAnesthesiaTimeRange || asaCodesSetting.visible);
  const hasVisibleField = icd10Setting.visible || cptSetting.visible || showAsaBlock;
  if (!hasVisibleField) return null;

  return (
    <Box sx={showDividerAbove ? { mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' } : undefined}>
      <SubblockHeading icon={<DataObjectRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />} title={title} />

      {/* ICD-10 / Diagnosis */}
      {icd10Setting.visible && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
            <Typography variant="body2" fontWeight={500} fontSize={13} sx={{ color: '#475569' }}>
              ICD-10/ Diagnosis
            </Typography>
            {!icd10Setting.required && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>(optional)</Typography>
            )}
          </Box>
          <Controller
            name={`${fieldPrefix}.icd10`}
            control={ctrl}
            render={({ field }) => {
              const current = (field.value as string[]) ?? [];
              const pending = (suggestions?.icd10 ?? []).filter(
                (s) => !current.some((v) => v.trim().toLowerCase() === s.value.trim().toLowerCase()),
              );
              return (
                <>
                  <ChipInput
                    value={current}
                    onChange={field.onChange}
                    placeholder={current.length > 0 ? 'Add another' : 'Ex N80.9'}
                    helperText="Press Enter or comma to add each code"
                    ariaLabel="Add ICD-10 code"
                    disabled={locked}
                    codeType="ICD10"
                    />
                  {pending.map((s, i) => (
                    <Box key={`icd10-sugg-${i}`} sx={{ mt: 0.5 }}>
                      <ScalarSuggestion
                        candidate={s}
                        currentValue=""
                        onAccept={(v) => field.onChange([...current, v])}
                        label="ICD-10 / Diagnosis"
                        locked={locked}
                        onLocate={onLocate}
                      />
                    </Box>
                  ))}
                </>
              );
            }}
          />
          {codingErrs.icd10 && (
            <Typography variant="caption" color="error">
              {codingErrs.icd10.message as string}
            </Typography>
          )}
        </Box>
      )}

      {/* CPT Codes */}
      {cptSetting.visible && (
        <Box sx={{ mb: 2 }}>
          <CodeRows
            control={ctrl}
            name={`${fieldPrefix}.cpt`}
            codeColLabel="CPT Code"
            modifiersLabel="Modifiers"
            addLabel="Add CPT Code"
            hideAddButton={hideAddButton}
            codePlaceholder="e.g. 27447"
            suggestions={suggestions?.cpt}
            onLocate={onLocate}
            locked={locked || hideAddButton}
            rowErrors={Array.isArray(codingErrs.cpt) ? codingErrs.cpt : undefined}
            codeType="CPT"
            />
        </Box>
      )}

      {/* ASA block — only for the Anesthesia claim type */}
      {showAsaBlock && (
        <Box>
          {asaClassificationSetting.visible && (
            <Grid container spacing={GRID_SPACING} sx={{ mb: 2 }}>
              <Grid size={stacked ? 12 : COL_2}>
                <FieldLabel required={asaClassificationSetting.required}>ASA Classification</FieldLabel>
                <Controller
                  name={`${fieldPrefix}.asaClassification`}
                  control={ctrl}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        select
                        fullWidth
                        size="small"
                        disabled={locked}
                        error={!!codingErrs.asaClassification}
                        SelectProps={{ native: true }}
                      >
                        <option value="">Choose</option>
                        {asaOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </TextField>
                      <ScalarSuggestion
                        candidate={suggestions?.asaClassification}
                        currentValue={field.value ?? ''}
                        onAccept={field.onChange}
                        label="ASA Classification"
                        locked={locked}
                        onLocate={onLocate}
                      />
                    </>
                  )}
                />
                {codingErrs.asaClassification && (
                  <Typography variant="caption" color="error">
                    {codingErrs.asaClassification.message as string}
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}

          {showAnesthesiaTimeRange && (
            <>
              <AnesthesiaTimeRange
                startDate={startDateCtrl.field.value ?? ''}
                startTime={startTimeCtrl.field.value ?? ''}
                endDate={endDateCtrl.field.value ?? ''}
                endTime={endTimeCtrl.field.value ?? ''}
                dateOfService={dateOfService}
                startDateError={startDateCtrl.fieldState.error?.message}
                startTimeError={startTimeCtrl.fieldState.error?.message}
                endDateError={endDateCtrl.fieldState.error?.message}
                endTimeError={endTimeCtrl.fieldState.error?.message}
                onChange={({
                  anesthesiaStartDate,
                  anesthesiaStartTime,
                  anesthesiaEndDate,
                  anesthesiaEndTime,
                }: AnesthesiaTimeRangeFields) => {
                  startDateCtrl.field.onChange(anesthesiaStartDate);
                  startTimeCtrl.field.onChange(anesthesiaStartTime);
                  endDateCtrl.field.onChange(anesthesiaEndDate);
                  endTimeCtrl.field.onChange(anesthesiaEndTime);
                }}
                disabled={locked}
                showStartDate={anesthesiaStartDateSetting.visible}
                showStartTime={anesthesiaStartTimeSetting.visible}
                showEndDate={anesthesiaEndDateSetting.visible}
                showEndTime={anesthesiaEndTimeSetting.visible}
                stacked={stacked}
                showTimeHint={showTimeHint}
                // Each suggestion is handed to the field it belongs to and renders directly
                // beneath that input. They used to be emitted as one block below the whole
                // range, which left four stacked suggestions with nothing tying each to its
                // field. The visibility guards are unchanged — a hidden field offers none.
                startDateSuggestion={
                  anesthesiaStartDateSetting.visible && (
                    <ScalarSuggestion
                      candidate={suggestions?.anesthesiaStartDate}
                      currentValue={startDateCtrl.field.value ?? ''}
                      onAccept={startDateCtrl.field.onChange}
                      displayValue={suggestions?.anesthesiaStartDate ? formatDateForDisplay(suggestions.anesthesiaStartDate.value) : undefined}
                      label="Anesthesia Start Date"
                      locked={locked}
                      onLocate={onLocate}
                    />
                  )
                }
                startTimeSuggestion={
                  anesthesiaStartTimeSetting.visible && (
                    <ScalarSuggestion
                      candidate={suggestions?.anesthesiaStartTime}
                      currentValue={startTimeCtrl.field.value ?? ''}
                      onAccept={startTimeCtrl.field.onChange}
                      label="Anesthesia Start Time"
                      locked={locked}
                      onLocate={onLocate}
                    />
                  )
                }
                endDateSuggestion={
                  anesthesiaEndDateSetting.visible && (
                    <ScalarSuggestion
                      candidate={suggestions?.anesthesiaEndDate}
                      currentValue={endDateCtrl.field.value ?? ''}
                      onAccept={endDateCtrl.field.onChange}
                      displayValue={suggestions?.anesthesiaEndDate ? formatDateForDisplay(suggestions.anesthesiaEndDate.value) : undefined}
                      label="Anesthesia End Date"
                      locked={locked}
                      onLocate={onLocate}
                    />
                  )
                }
                endTimeSuggestion={
                  anesthesiaEndTimeSetting.visible && (
                    <ScalarSuggestion
                      candidate={suggestions?.anesthesiaEndTime}
                      currentValue={endTimeCtrl.field.value ?? ''}
                      onAccept={endTimeCtrl.field.onChange}
                      label="Anesthesia End Time"
                      locked={locked}
                      onLocate={onLocate}
                    />
                  )
                }
              />
            </>
          )}

          {asaCodesSetting.visible && (
            <CodeRows
              control={ctrl}
              name={`${fieldPrefix}.asaCodes`}
              codeColLabel="ASA Code"
              modifiersLabel="Modifiers"
              addLabel="Add ASA Code"
              codePlaceholder="e.g. 00790"
              suggestions={suggestions?.asaCodes}
              onLocate={onLocate}
              locked={locked}
              rowErrors={Array.isArray(codingErrs.asaCodes) ? codingErrs.asaCodes : undefined}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
